import axios, {
  AxiosError,
  type AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { HOST_URL } from "config";
import { PATH } from "routes/paths";
import type { CustomResponseFormat } from "api/instance";

// 레퍼럴 앰버서더(인플루언서) 대시보드 API.
// 앰버서더 토큰은 유저 인증(X-Auth-Token/유저 Bearer)과 완전 별개 체계라 기존 두 인스턴스를 쓰지 않는다 —
// 각 인스턴스의 401 처리(/login·/promotion-login 리다이렉트, 토큰 리프레쉬)가 앰버서더 플로우와 맞지 않기 때문.

export const AMBASSADOR_TOKEN_STORAGE_KEY = "AMBASSADOR_TOKEN";

export const getAmbassadorToken = () =>
  localStorage.getItem(AMBASSADOR_TOKEN_STORAGE_KEY);
export const setAmbassadorToken = (token: string) =>
  localStorage.setItem(AMBASSADOR_TOKEN_STORAGE_KEY, token);
export const clearAmbassadorToken = () =>
  localStorage.removeItem(AMBASSADOR_TOKEN_STORAGE_KEY);

export const REFERRAL_AMBASSADOR_DOMAIN = "referral-ambassador";

export const REFERRAL_AMBASSADOR_SIGNUP_ENDPOINT = "signup";
export const REFERRAL_AMBASSADOR_VERIFY_ENDPOINT = "verify";
export const REFERRAL_AMBASSADOR_LOGIN_ENDPOINT = "login";
export const REFERRAL_AMBASSADOR_RESEND_ENDPOINT = "verification/resend";
export const REFERRAL_AMBASSADOR_DASHBOARD_ENDPOINT = "dashboard";
export const REFERRAL_AMBASSADOR_PASSWORD_CHANGE_ENDPOINT = "password/change";

// 서버 MessageCode — 미인증 계정 (로그인 시도 시 이메일 인증 단계로 유도)
export const REFERRAL_AMBASSADOR_CODE_NOT_VERIFIED = 76;

export type ReferralAmbassadorTokenResult = {
  accessToken: string;
  ambassadorName?: string | null;
};

export type ReferralAmbassadorStatsSummary = {
  signupCount: number;
  payerCount: number;
  refundCount: number;
  // 정산액(원) — 코드별 단가(결제자 1인당 정액)가 설정된 경우 monthly/total 에만 채워진다. daily 는 항상 null.
  settlementAmount?: number | null;
};

// period 는 daily 면 "2026-08-03", monthly 면 "2026-08" (서버 최신순 정렬)
export type ReferralAmbassadorStatsBucket = ReferralAmbassadorStatsSummary & {
  period: string;
};

// 가입자 = 링크로 유입되어 코드가 귀속된 유저, 결제자 = 정산 기여 확정 유저, 환불자 = 그중 결제 취소 유저
export type ReferralAmbassadorDashboardResult = {
  ambassadorName?: string | null;
  // 로그인 계정 이메일 (계정 관리 메뉴 표시용)
  email?: string | null;
  codes: string[];
  total: ReferralAmbassadorStatsSummary;
  daily: ReferralAmbassadorStatsBucket[];
  monthly: ReferralAmbassadorStatsBucket[];
};

interface AmbassadorInstance extends AxiosInstance {
  get<T>(url: string, config?: AxiosRequestConfig): Promise<T>;
  post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T>;
}

export const ambassadorAxios: AmbassadorInstance = axios.create({
  baseURL: HOST_URL,
  timeout: 15000, // 15초 타임아웃
});

// 매 요청 시점에 localStorage 를 읽어 토큰을 부착한다 (로그인 직후/다른 탭 갱신에 의한 stale 방지).
const onRequest = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  const token = getAmbassadorToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
};

// 비즈니스 오류는 HTTP 200 + code≠0 이므로 envelope 를 그대로 반환한다 — code 검사는 호출부 책임.
const onResponse = (response: AxiosResponse): AxiosResponse => {
  return response.data;
};

// 앰버서더 토큰 무효/만료(HTTP 401)면 토큰을 지우고 로그인으로 보낸다. 로그인 페이지에서는 리다이렉트하지 않는다(루프 방지).
const onRejected = (error: AxiosError) => {
  if (error.response?.status === 401) {
    clearAmbassadorToken();
    if (window.location.pathname !== PATH.ambassadorLogin) {
      window.location.href = PATH.ambassadorLogin;
    }
  }
  return Promise.reject(error);
};

ambassadorAxios.interceptors.request.use(onRequest);
ambassadorAxios.interceptors.response.use(onResponse, onRejected);

export const ambassadorApi = {
  // 사전 등록(referral_code.ambassador_email) 이메일만 가입 가능 — 성공 시 인증 코드 메일 발송
  signup: (email: string, password: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_AMBASSADOR_DOMAIN}/${REFERRAL_AMBASSADOR_SIGNUP_ENDPOINT}`,
      { email, password }
    ),

  // 이메일 인증 — 성공 시 토큰 발급(자동 로그인).
  // 비밀번호를 함께 재검증한다 (미인증 계정을 제3자 signup 이 덮어써 탈취하는 것 차단 — 서버 정책).
  verify: (email: string, password: string, code: string) =>
    ambassadorAxios.post<CustomResponseFormat<ReferralAmbassadorTokenResult>>(
      `${REFERRAL_AMBASSADOR_DOMAIN}/${REFERRAL_AMBASSADOR_VERIFY_ENDPOINT}`,
      {
        email,
        password,
        code,
      }
    ),

  login: (email: string, password: string) =>
    ambassadorAxios.post<CustomResponseFormat<ReferralAmbassadorTokenResult>>(
      `${REFERRAL_AMBASSADOR_DOMAIN}/${REFERRAL_AMBASSADOR_LOGIN_ENDPOINT}`,
      {
        email,
        password,
      }
    ),

  resendVerification: (email: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_AMBASSADOR_DOMAIN}/${REFERRAL_AMBASSADOR_RESEND_ENDPOINT}`,
      { email }
    ),

  dashboard: () =>
    ambassadorAxios.get<
      CustomResponseFormat<ReferralAmbassadorDashboardResult>
    >(
      `${REFERRAL_AMBASSADOR_DOMAIN}/${REFERRAL_AMBASSADOR_DASHBOARD_ENDPOINT}`
    ),

  // 로그인 상태에서 비밀번호 변경 — 현재 비밀번호 재검증 (계정 관리 메뉴)
  changePassword: (currentPassword: string, newPassword: string) =>
    ambassadorAxios.post<CustomResponseFormat<void>>(
      `${REFERRAL_AMBASSADOR_DOMAIN}/${REFERRAL_AMBASSADOR_PASSWORD_CHANGE_ENDPOINT}`,
      { currentPassword, newPassword }
    ),
};
